import { useState, FormEvent } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const contactInfo = [
  {
    icon: 'ri-map-pin-2-line',
    label: 'Adresse',
    value: '57 Rue Lecourbe\n75015 Paris, France',
    href: 'https://maps.google.com/?q=57+Rue+Lecourbe+75015+Paris',
  },
  {
    icon: 'ri-phone-line',
    label: 'Téléphone',
    value: '+33 7 58 86 05 74',
    href: 'tel:+33758860574',
  },
  {
    icon: 'ri-mail-line',
    label: 'Email',
    value: 'mariuszcichorz@inbox.eu',
    href: 'mailto:mariuszcichorz@inbox.eu',
  },
];

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const form = e.currentTarget;
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value as string);
    });

    try {
      const res = await fetch('https://readdy.ai/api/form/d7211eplum2co2q1fvs0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
        form.reset();
        setCharCount(0);
      } else {
        setError('Une erreur est survenue. Veuillez réessayer.');
      }
    } catch {
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-[Manrope,sans-serif]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-40 pb-20 bg-[#0A1628] overflow-hidden">
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
        <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <span className="inline-block px-4 py-1.5 border border-white/20 rounded-full text-xs font-semibold tracking-widest uppercase text-white/50 mb-6">
            Contact
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Discutons de votre projet</h1>
          <p className="text-white/60 text-lg max-w-xl mx-auto">
            Contactez-nous pour toute demande. Nous vous répondons dans les meilleurs délais.
          </p>
        </div>
      </section>

      {/* Contact cards */}
      <section className="py-16 bg-[#F7F8FA]">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {contactInfo.map((info) => (
              <a
                key={info.label}
                href={info.href}
                target={info.label === 'Adresse' ? '_blank' : undefined}
                rel="noopener noreferrer"
                className="bg-white rounded-2xl p-8 flex flex-col items-center text-center hover:-translate-y-1 transition-all duration-300 cursor-pointer group"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-[#0A1628] rounded-xl mb-4 group-hover:bg-[#1a2f4f] transition-colors">
                  <i className={`${info.icon} text-white text-xl`} />
                </div>
                <p className="text-xs font-semibold tracking-widest uppercase text-[#6B7280] mb-2">{info.label}</p>
                <p className="text-[#0A1628] font-medium text-sm leading-relaxed whitespace-pre-line">{info.value}</p>
              </a>
            ))}
          </div>

          {/* SIRET info */}
          <div className="mt-6 bg-white rounded-2xl p-6 flex items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center bg-[#F7F8FA] rounded-xl">
              <i className="ri-building-line text-[#0A1628] text-lg" />
            </div>
            <div>
              <p className="text-xs font-semibold tracking-widest uppercase text-[#6B7280]">Entreprise</p>
              <p className="text-[#0A1628] font-medium text-sm">
                CICHORZ — Mariusz Slawomir Cichorz — SIRET : 102 737 178 00016
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-20 bg-white">
        <div className="max-w-2xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-[#0A1628] mb-3">Envoyez-nous un message</h2>
            <p className="text-[#6B7280] text-sm">Décrivez votre projet et nous vous contacterons rapidement.</p>
          </div>

          {submitted ? (
            <div className="bg-[#F7F8FA] rounded-2xl p-12 text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6 bg-[#0A1628] rounded-full">
                <i className="ri-check-line text-white text-2xl" />
              </div>
              <h3 className="text-2xl font-bold text-[#0A1628] mb-3">Message envoyé !</h3>
              <p className="text-[#6B7280] text-sm leading-relaxed max-w-sm mx-auto">
                Merci pour votre message. Nous vous répondrons dans les meilleurs délais à l&apos;adresse email indiquée.
              </p>
            </div>
          ) : (
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="flex flex-col gap-6 bg-[#F7F8FA] rounded-2xl p-8"
              id="contact-form"
            >
              {/* Nom */}
              <div className="flex flex-col gap-2">
                <label htmlFor="nom" className="text-xs font-semibold tracking-widest uppercase text-[#374151]">
                  Nom <span className="text-red-500">*</span>
                </label>
                <input
                  id="nom"
                  name="nom"
                  type="text"
                  required
                  placeholder="Votre nom complet"
                  className="bg-white border border-gray-200 rounded-xl px-5 py-4 text-sm text-[#0A1628] placeholder-gray-400 focus:outline-none focus:border-[#0A1628] transition-colors"
                />
              </div>

              {/* Email */}
              <div className="flex flex-col gap-2">
                <label htmlFor="email" className="text-xs font-semibold tracking-widest uppercase text-[#374151]">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="votre@email.com"
                  className="bg-white border border-gray-200 rounded-xl px-5 py-4 text-sm text-[#0A1628] placeholder-gray-400 focus:outline-none focus:border-[#0A1628] transition-colors"
                />
              </div>

              {/* Téléphone */}
              <div className="flex flex-col gap-2">
                <label htmlFor="telephone" className="text-xs font-semibold tracking-widest uppercase text-[#374151]">
                  Téléphone
                </label>
                <input
                  id="telephone"
                  name="telephone"
                  type="tel"
                  placeholder="+33 6 00 00 00 00"
                  className="bg-white border border-gray-200 rounded-xl px-5 py-4 text-sm text-[#0A1628] placeholder-gray-400 focus:outline-none focus:border-[#0A1628] transition-colors"
                />
              </div>

              {/* Description */}
              <div className="flex flex-col gap-2">
                <label htmlFor="description" className="text-xs font-semibold tracking-widest uppercase text-[#374151]">
                  Description du projet <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="Décrivez votre projet, vos besoins, votre budget estimé..."
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="bg-white border border-gray-200 rounded-xl px-5 py-4 text-sm text-[#0A1628] placeholder-gray-400 focus:outline-none focus:border-[#0A1628] transition-colors resize-none"
                />
                <p className={`text-xs text-right ${charCount >= 500 ? 'text-red-500' : 'text-gray-400'}`}>
                  {charCount}/500 caractères
                </p>
              </div>

              {error && (
                <p className="text-red-500 text-sm text-center">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading || charCount >= 500}
                className="w-full py-4 bg-[#0A1628] text-white font-bold text-sm tracking-widest uppercase rounded-xl hover:bg-[#1a2f4f] transition-all duration-200 flex items-center justify-center gap-3 disabled:opacity-60 cursor-pointer whitespace-nowrap"
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-base" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    Envoyer le message
                    <i className="ri-send-plane-line text-base" />
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Map */}
      <section className="pb-0">
        <div className="w-full h-72">
          <iframe
            title="CICHORZ - 57 Rue Lecourbe, Paris"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2625.9213!2d2.3013!3d48.8418!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e6703498e6f89b%3A0x2f8ed5e3e3e3e3e3!2s57%20Rue%20Lecourbe%2C%2075015%20Paris%2C%20France!5e0!3m2!1sfr!2sfr!4v1700000000000!5m2!1sfr!2sfr"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </section>

      <Footer />
    </div>
  );
}
